import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@availity/element';
import checkIcon from '../../../static/assets/icons/check.svg';
import payersIcon from '../../../static/assets/illustrations/API-Marketplace-Payer-Network.png';
import divider from '../../../static/assets/icons/Divider-4.svg';
import divider5 from '../../../static/assets/icons/Divider-5.svg';
import customerFocusBg from '../../../static/assets/backgrounds/customer-focus-bg.jpg';
import Animation from '../../Utils/components/Animation';

const BenefitItem = ({ text, description }) => (
  <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, alignItems: 'flex-start' }}>
    <img 
      src={checkIcon} 
      alt="" 
      style={{  
        width: 18,
        height: 22,
        filter: 'brightness(0) saturate(100%) invert(32%) sepia(56%) saturate(1547%) hue-rotate(349deg) brightness(93%) contrast(90%)',
        paddingTop: 4,
      }} 
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        sx={{
          color: '#0D2543',
          fontSize: { xs: 18, md: 20 },
          fontFamily: 'Montserrat',
          fontWeight: 600,
          lineHeight: 26/18,
        }}
      >
        {text}
      </Typography>
      <Typography
        sx={{
          color: '#0D2543',
          fontSize: { xs: 16, md: 18 },
          fontFamily: 'Lato',
          fontWeight: 400,
          lineHeight: 26/18,
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

BenefitItem.propTypes = {
  text: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const PayerNetworkVisual = () => (
 <img src={payersIcon} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
);

const CustomerFocusSection = () => (
  <Box
    className="customer-focus"
    sx={{
      width: 1,
      py: { xs: 8, md: 16.5 },
      gap: { xs: 2, md: 3 },
      '&::before': {
        backgroundImage: `url(${customerFocusBg})`,
      },
    }}
  >
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            color: '#BF4D17',
            fontSize: { xs: 18, md: 18 },
            fontFamily: 'Lato',
            fontWeight: 400,
            lineHeight: 26/18,
          }}
        >
          From Payers to Patients
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            color: '#0D2543',
            fontSize: { xs: 28, md: 36 },
            fontFamily: 'Montserrat',
            fontWeight: 600,
            lineHeight: 48/36,
          }}
        >
          Transforming Healthcare Data: Real-Time, Compliant,<br />
          and Scalable Connectivity for Payers
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: { xs: 5, lg: 0 },
        }}
      >
        <Box
          sx={{
            width: { xs: 1, lg: 602 },
            display: 'flex',
            flexDirection: 'column',
            gap: 4.875,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BenefitItem text="Improved Interoperability" description="APIs allow seamless data exchange between payers and providers." />
            <Box component="img" src={divider5} alt="" sx={{ width: 'fit-content' }} />
            <BenefitItem text="Reduced Administrative Burden" description="Accelerate eligibility checks, claims, and authorizations workflows." />
            <Box component="img" src={divider5} alt="" sx={{ width: 'fit-content' }} />
            <BenefitItem text="Enhanced Member Experience" description="Real-time access to benefits and claims improves transparency."/>
            <Box component="img" src={divider5} alt="" sx={{ width: 'fit-content' }} />
            <BenefitItem text="Regulatory Compliance" description="Supports HIPAA and CMS interoperability mandates."/>
            <Box component="img" src={divider5} alt="" sx={{ width: 'fit-content' }} />
            <BenefitItem text="Innovate Faster" description="Adopt new APIs quickly without rebuilding legacy systems. "/>
          </Box>
        </Box>

        <Box sx={{ display: 'block', width: { xs: 1, lg: 'auto' } }}>
          <Animation
            src="/static/assets/illustrations/Animation_Network.html"
            sx={{ width: { xs: '100%', lg: 550 }, aspectRatio: '1 / 1' }}
          />
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        width: { xs: 'calc(100% - 32px)', lg: 1200 },
        maxWidth: 1200,
        mx: { xs: '16px', lg: 'auto' },
        background: '#EFF3FF',
        boxShadow: '0px 3px 30px 2px #C7D4F8',
        borderRadius: 3,
        border: '1px solid #91B0DA',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1.3,
          px: { xs: 5, sm: 10, lg: 10 },
          py: { xs: 10, sm: 10, lg: 11.125 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            sx={{
              color: '#0D2543',
              fontSize: { xs: 20, md: 24 },
              fontFamily: 'Montserrat',
              fontWeight: 600,
              lineHeight: 32/24,
              textAlign: { xs: 'center', md: 'center', lg: 'left' },
            }}
          >
            Tap Into Availity&apos;s Expansive Payer Network
          </Typography>
          <Box component="img" src={divider} alt="" sx={{ width: 'fit-content', alignSelf: { xs: 'center', md: 'center', lg: 'flex-start' } }} />
          <Typography
            sx={{
              color: '#0D2543',
              fontSize: { xs: 16, md: 18 },
              fontFamily: 'Lato',
              fontWeight: 400,
              lineHeight: '26px',
              letterSpacing: 0,
              textAlign: { xs: 'center', md: 'center', lg: 'left' },
            }}
          >
            Gain instant access to a vast network of payers through Availity&apos;s Unified API Marketplace. Simplify integrations, accelerate development, and unlock opportunities to connect with leading healthcare organizations—all in one place.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex' }}>
        <PayerNetworkVisual />
      </Box>
    </Box>
  </Box>
);

export default CustomerFocusSection;